<script lang="ts">
	import { schema } from '$lib/schemas/dividend-calculator';
	import dayjs from 'dayjs';
	import isBetween from 'dayjs/plugin/isBetween';
	import { onMount } from 'svelte';
	import { defaults, superForm } from 'sveltekit-superforms';
	import { zod } from 'sveltekit-superforms/adapters';
	import AmountInput from './AmountInput.svelte';
	import ButtonSubmit from './ButtonSubmit.svelte';
	import Collapsible from './Collapsible.svelte';

	interface Props {
		name: string;
		currentPrice: number;
		dividendRate: number;
		currency: string;
	}

	let { name, currentPrice, dividendRate, currency }: Props = $props();

	dayjs.extend(isBetween);

	let totalDividends: number | undefined = $state();

	const { form, errors, enhance, allErrors, isTainted, tainted, submitting, delayed } = superForm(
		defaults(zod(schema)),
		{
			SPA: true,
			validators: zod(schema),
			dataType: 'json',
			resetForm: false,
			onUpdate({ form }) {
				if (form.valid) {
					totalDividends = form.data.numOfShares * dividendRate;
				}
			}
		}
	);

	onMount(() => {
		form.set({ numOfShares: 100 });
	});

	let amount = $derived($form.numOfShares * currentPrice);
</script>

<div class="mx-auto max-w-sm">
	<form use:enhance>
		<label for="numOfShares" class="h3 mb-6 block text-center"
			>Enter no. of shares you hold for <br />{name}</label
		>
		<AmountInput
			id="numOfShares"
			bind:value={$form.numOfShares}
			class="mb-8"
			errors={$errors.numOfShares}
		></AmountInput>
		{#if amount}
			<p class="index-regular text-gray-dark">
				Investment amount you will need to buy {$form.numOfShares} shares now
			</p>
			<p class="h3">
				{amount.toLocaleString('default', {
					style: 'currency',
					currency,
					currencyDisplay: 'narrowSymbol'
				})}
			</p>
		{/if}

		<ButtonSubmit
			disabled={$allErrors.length > 0 || !isTainted($tainted)}
			class="mx-auto mt-8 w-full"
			submitting={$submitting}
			delayed={$delayed}>Calculate dividend payment</ButtonSubmit
		>
	</form>

	{#if totalDividends && !isTainted($tainted)}
		<div class="bg-green-dark text-white-pure mt-8 mb-3 rounded-lg p-5 text-center">
			<p class="h1-serif">
				{totalDividends.toLocaleString('default', {
					style: 'currency',
					currency: currency,
					currencyDisplay: 'narrowSymbol'
				})}
			</p>
			<p>in dividends per year</p>
		</div>

		<div class="index-regular border-gray-dark mb-5 rounded-lg border-1 p-5">
			<p class="mb-3">Based on your input:</p>

			<p class="mb-3">
				<span class="index-bold"
					>{$form.numOfShares.toLocaleString('default', {
						style: 'decimal'
					})} shares</span
				>
				x {dividendRate.toLocaleString('default', {
					style: 'currency',
					currency: currency,
					maximumFractionDigits: 3,
					currencyDisplay: 'narrowSymbol'
				})} ({dayjs().year()} Forecast Dividend per Share) =
				<span class="index-bold"
					>{totalDividends.toLocaleString('default', {
						style: 'currency',
						currency: currency,
						currencyDisplay: 'narrowSymbol'
					})}</span
				>
			</p>
			<p class="mb-3">
				The <span class="index-bold">investment amount required</span> is calculated based on the current
				share price
			</p>
			<p>
				<span class="index-bold"
					>{$form.numOfShares.toLocaleString('default', {
						style: 'decimal'
					})} shares</span
				>
				x {currentPrice.toLocaleString('default', {
					style: 'currency',
					currency: currency,
					currencyDisplay: 'narrowSymbol'
				})} per share =
				<span class="index-bold"
					>{amount.toLocaleString('default', {
						style: 'currency',
						currency: currency,
						currencyDisplay: 'narrowSymbol'
					})}</span
				>
			</p>
		</div>

		<Collapsible title="Read the disclaimer">
			<ul class="small-regular text-gray-dark ml-5 list-disc">
				<li>
					The amount shown is for illustration purposes only, and actual dividend earned may differ
					because of various factors.
				</li>
				<li>Past performance is not a guarantee of future returns.</li>
				<li>
					All investments carry risk, including the risk that the investor may lose all or part of
					his investment.
				</li>
			</ul>
		</Collapsible>
	{/if}
</div>
